import {Col, message, Modal, Row, Spin} from 'antd';
import {Button} from 'antd';
import {useEffect, useState} from 'react';
import api from '../../services/api';
import ENDPOINTS from '../../constants/endpoints';
import {formatPath} from '../../constants/PathRoutes';
import {BuildFieldErrorsDict} from '../../utils/utils';
import Form from '../data_entry/form/Form';
import InputFormField from '../data_entry/form/InputFormField';
import SelectFormField from '../data_entry/form/SelectFormField';
import CustomButton from '../data_entry/Button';

const UF_OPTIONS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
].map(uf => ({value: uf, label: uf}));

const FORM_NAMES = {
    CEP: 'postal_code',
    STREET: 'street',
    NUMBER: 'number',
    COMPLEMENT: 'complement',
    NEIGHBORHOOD: 'neighborhood',
    CITY: 'city',
    STATE: 'state',
};

export default function AddressModal({open, record, onClose, onSuccess}) {
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);

    useEffect(() => {
        if (open && record) {
            form.setFieldsValue({
                [FORM_NAMES.CEP]: record.postal_code,
                [FORM_NAMES.STREET]: record.street,
                [FORM_NAMES.NUMBER]: record.number,
                [FORM_NAMES.COMPLEMENT]: record.complement,
                [FORM_NAMES.NEIGHBORHOOD]: record.neighborhood,
                [FORM_NAMES.CITY]: record.city,
                [FORM_NAMES.STATE]: record.state,
            });
        }
    }, [open, record, form]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    const handleCepChange = async (value) => {
        const digits = value.replace(/\D/g, '');
        form.setFieldValue(FORM_NAMES.CEP, digits);
        if (digits.length !== 8) return;
        setCepLoading(true);
        try {
            const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();
            if (data.erro) {
                message.warning('CEP não encontrado');
                return;
            }
            form.setFieldsValue({
                [FORM_NAMES.STREET]: data.logradouro || '',
                [FORM_NAMES.NEIGHBORHOOD]: data.bairro || '',
                [FORM_NAMES.CITY]: data.localidade || '',
                [FORM_NAMES.STATE]: data.uf || '',
            });
        } catch {
            message.error('Erro ao buscar CEP');
        } finally {
            setCepLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setSubmitLoading(true);
        try {
            if (record?.id) {
                await api.patch(formatPath(ENDPOINTS.AUTH_ADDRESS_DETAIL, {id: record.id}), values);
                message.success('Endereço atualizado com sucesso');
            } else {
                await api.post(ENDPOINTS.AUTH_ADDRESS_LIST, values);
                message.success('Endereço cadastrado com sucesso');
            }
            handleClose();
            onSuccess?.();
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal
            title={record?.id ? 'Editar Endereço' : 'Novo Endereço'}
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
        >
            <Form form={form} onFinish={handleSubmit} style={{marginTop: 16}}>
                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={8}>
                        <InputFormField
                            maxLength={8}
                            placeholder="00000000"
                            suffix={cepLoading ? <Spin size="small"/> : null}
                            onChange={handleCepChange}
                            form={{name: FORM_NAMES.CEP, label: 'CEP', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} sm={16}>
                        <InputFormField
                            form={{name: FORM_NAMES.STREET, label: 'Rua', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <InputFormField
                            form={{name: FORM_NAMES.NUMBER, label: 'Número', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} sm={16}>
                        <InputFormField
                            form={{name: FORM_NAMES.COMPLEMENT, label: 'Complemento'}}
                        />
                    </Col>
                    <Col xs={24}>
                        <InputFormField
                            form={{name: FORM_NAMES.NEIGHBORHOOD, label: 'Bairro', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} sm={16}>
                        <InputFormField
                            form={{name: FORM_NAMES.CITY, label: 'Cidade', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <SelectFormField
                            options={UF_OPTIONS}
                            form={{name: FORM_NAMES.STATE, label: 'UF', rules: [{required: true}]}}
                        />
                    </Col>
                    <Col xs={24} style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8}}>
                        <Button onClick={handleClose}>Cancelar</Button>
                        <CustomButton htmlType="submit" loading={submitLoading}>
                            {record?.id ? 'Salvar' : 'Cadastrar'}
                        </CustomButton>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}